import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate, useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import Button from '../common/Button';
import ToastContainer from '../common/ToastContainer';
import { useToast } from '../../hooks/useToast';
import { useAuthSync } from '../../hooks/useAuthSync';
import { useOrganizationTimezone } from '../../hooks/useOrganizationTimezone';
import CalendarBlockService from '../../services/calendarBlockService';
import UserService from '../../services/userService';
import './CalendarBlocks.css';

const WEEKDAY_OPTIONS = [
  { value: 'MO', label: 'Lunes' },
  { value: 'TU', label: 'Martes' },
  { value: 'WE', label: 'Miércoles' },
  { value: 'TH', label: 'Jueves' },
  { value: 'FR', label: 'Viernes' },
  { value: 'SA', label: 'Sábado' },
  { value: 'SU', label: 'Domingo' }
];

const getInitialFormState = (doctorId) => ({
  doctorId: doctorId || '',
  mode: 'single',
  reason: '',
  allDay: false,
  durationMinutes: '30',
  startDate: '',
  startTime: '09:00',
  recurrenceStartDate: '',
  recurrenceFrequency: 'WEEKLY',
  recurrenceInterval: '1',
  recurrenceDays: ['MO'],
  recurrenceUntil: ''
});

const parseRRule = (rrule, timezone, fallbackStartTime = '09:00') => {
  const lines = (rrule || '').split('\n').filter(Boolean);
  const dtstartLine = lines.find((line) => line.startsWith('DTSTART:'));
  const rruleLine = lines.find((line) => line.startsWith('RRULE:')) || '';
  const entries = rruleLine.replace('RRULE:', '').split(';').reduce((accumulator, part) => {
    const [key, value] = part.split('=');
    if (key && value) {
      accumulator[key] = value;
    }
    return accumulator;
  }, {});

  let recurrenceStartDate = '';
  if (dtstartLine) {
    const parsed = DateTime.fromFormat(dtstartLine.replace('DTSTART:', ''), "yyyyMMdd'T'HHmmss'Z'", { zone: 'utc' }).setZone(timezone);
    if (parsed.isValid) {
      recurrenceStartDate = parsed.toFormat('yyyy-MM-dd');
    }
  }

  let recurrenceUntil = '';
  if (entries.UNTIL) {
    const parsed = DateTime.fromFormat(entries.UNTIL, "yyyyMMdd'T'HHmmss'Z'", { zone: 'utc' }).setZone(timezone);
    if (parsed.isValid) {
      recurrenceUntil = parsed.toFormat('yyyy-MM-dd');
    }
  }

  return {
    recurrenceStartDate,
    recurrenceFrequency: entries.FREQ || 'WEEKLY',
    recurrenceInterval: entries.INTERVAL || '1',
    recurrenceDays: entries.BYDAY ? entries.BYDAY.split(',') : ['MO'],
    recurrenceUntil,
    startTime: fallbackStartTime
  };
};

const buildSingleStartDateTime = (date, time, timezone) => {
  return DateTime.fromFormat(`${date} ${time}`, 'yyyy-MM-dd HH:mm', { zone: timezone }).toISO();
};

const buildRRule = (formData, timezone) => {
  const dtstart = DateTime.fromFormat(
    `${formData.recurrenceStartDate} ${formData.startTime}`,
    'yyyy-MM-dd HH:mm',
    { zone: timezone }
  ).toUTC().toFormat("yyyyMMdd'T'HHmmss'Z'");

  const parts = [
    `FREQ=${formData.recurrenceFrequency}`,
    `INTERVAL=${Math.max(1, Number(formData.recurrenceInterval || 1))}`
  ];

  if (formData.recurrenceFrequency === 'WEEKLY') {
    const selectedDays = formData.recurrenceDays.length > 0 ? formData.recurrenceDays : ['MO'];
    parts.push(`BYDAY=${selectedDays.join(',')}`);
  }

  if (formData.recurrenceFrequency === 'MONTHLY' && formData.recurrenceStartDate) {
    const dayOfMonth = formData.recurrenceStartDate.split('-')[2];
    parts.push(`BYMONTHDAY=${Number(dayOfMonth)}`);
  }

  if (formData.recurrenceUntil) {
    const until = DateTime.fromFormat(`${formData.recurrenceUntil} 23:59`, 'yyyy-MM-dd HH:mm', { zone: timezone })
      .toUTC()
      .toFormat("yyyyMMdd'T'HHmmss'Z'");
    parts.push(`UNTIL=${until}`);
  }

  return `DTSTART:${dtstart}\nRRULE:${parts.join(';')}`;
};

const CalendarBlockForm = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { doctorId, blockId } = useParams();
  const isEdit = Boolean(blockId);
  const { timezone, isLoading: timezoneLoading } = useOrganizationTimezone();
  const { toasts, removeToast, error: showError } = useToast();
  const { hasPermission, user: authUser } = useAuthSync();
  const canViewAll = hasPermission('agenda:viewall');
  const [doctors, setDoctors] = useState([]);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingBlock, setIsLoadingBlock] = useState(isEdit);
  const [existingBlock, setExistingBlock] = useState(null);
  const [formData, setFormData] = useState(() => getInitialFormState(doctorId));

  const isAllDay = formData.allDay;

  const selectedDoctorName = useMemo(() => {
    const doctor = doctors.find((item) => (item.id || item.userId) === formData.doctorId);
    return doctor?.fullName || doctor?.name || doctor?.username || doctor?.email || 'doctor';
  }, [doctors, formData.doctorId]);

  const loadDoctors = useCallback(async () => {
    if (!auth.user?.access_token) return;

    setIsLoadingDoctors(true);
    try {
      if (!canViewAll && authUser) {
        const resolvedDoctorId = authUser.id || authUser.userId || auth.user?.profile?.sub || doctorId || '';
        setDoctors([authUser]);
        setFormData((currentValue) => ({
          ...currentValue,
          doctorId: currentValue.doctorId || resolvedDoctorId
        }));
        return;
      }

      const users = await UserService.getOrganizationUsers(auth.user.access_token);
      const resolvedDoctors = (users || []).filter((user) => Boolean(user.id || user.userId));
      setDoctors(resolvedDoctors);
      setFormData((currentValue) => ({
        ...currentValue,
        doctorId: currentValue.doctorId || doctorId || resolvedDoctors[0]?.id || resolvedDoctors[0]?.userId || ''
      }));
    } catch (error) {
      showError(error.message || 'No se pudieron cargar los doctores');
    } finally {
      setIsLoadingDoctors(false);
    }
  }, [auth.user?.access_token, auth.user?.profile?.sub, authUser, canViewAll, doctorId, showError]);

  const loadExistingBlock = useCallback(async () => {
    if (!isEdit || !auth.user?.access_token || !doctorId || timezoneLoading) return;

    setIsLoadingBlock(true);
    try {
      const data = await CalendarBlockService.getCalendarBlocks(doctorId, auth.user.access_token);
      const block = (data || []).find((item) => item.id === blockId);

      if (!block) {
        throw new Error('Bloqueo no encontrado');
      }

      setExistingBlock(block);

      if (block.type === 'BLOCK_SINGLE') {
        const parsed = DateTime.fromISO(block.startDateTime).setZone(timezone);
        setFormData({
          doctorId: block.doctorId,
          mode: 'single',
          reason: block.reason || '',
          allDay: Boolean(block.allDay),
          durationMinutes: String(block.durationMinutes || 30),
          startDate: parsed.isValid ? parsed.toFormat('yyyy-MM-dd') : '',
          startTime: parsed.isValid ? parsed.toFormat('HH:mm') : '09:00',
          recurrenceStartDate: '',
          recurrenceFrequency: 'WEEKLY',
          recurrenceInterval: '1',
          recurrenceDays: ['MO'],
          recurrenceUntil: ''
        });
        return;
      }

      const recurring = parseRRule(block.rrule, timezone, block.startTime);
      setFormData({
        doctorId: block.doctorId,
        mode: 'recurring',
        reason: block.reason || '',
        allDay: Boolean(block.allDay),
        durationMinutes: String(block.durationMinutes || 30),
        startDate: '',
        startTime: block.startTime || recurring.startTime || '09:00',
        recurrenceStartDate: recurring.recurrenceStartDate || DateTime.now().setZone(timezone).toFormat('yyyy-MM-dd'),
        recurrenceFrequency: recurring.recurrenceFrequency,
        recurrenceInterval: recurring.recurrenceInterval,
        recurrenceDays: recurring.recurrenceDays,
        recurrenceUntil: recurring.recurrenceUntil
      });
    } catch (error) {
      showError(error.message || 'No se pudo cargar el bloqueo');
    } finally {
      setIsLoadingBlock(false);
    }
  }, [auth.user?.access_token, blockId, doctorId, isEdit, showError, timezone, timezoneLoading]);

  useEffect(() => {
    loadDoctors();
  }, [loadDoctors]);

  useEffect(() => {
    loadExistingBlock();
  }, [loadExistingBlock]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((currentValue) => ({
      ...currentValue,
      [name]: value
    }));
  };

  const handleAllDayChange = (event) => {
    const checked = event.target.checked;

    setFormData((currentValue) => ({
      ...currentValue,
      allDay: checked,
      durationMinutes: checked ? '1440' : (currentValue.durationMinutes === '1440' ? '30' : currentValue.durationMinutes),
      startTime: checked ? '00:00' : currentValue.startTime
    }));
  };

  const handleToggleMode = (mode) => {
    setFormData((currentValue) => ({
      ...currentValue,
      mode
    }));
  };

  const handleWeekdayToggle = (dayValue) => {
    setFormData((currentValue) => {
      const exists = currentValue.recurrenceDays.includes(dayValue);
      const recurrenceDays = exists
        ? currentValue.recurrenceDays.filter((value) => value !== dayValue)
        : [...currentValue.recurrenceDays, dayValue];

      return {
        ...currentValue,
        recurrenceDays
      };
    });
  };

  const validateForm = () => {
    if (!formData.doctorId) {
      showError('Selecciona un doctor');
      return false;
    }

    if (!formData.allDay && (!formData.durationMinutes || Number(formData.durationMinutes) <= 0)) {
      showError('La duración debe ser mayor a 0 minutos');
      return false;
    }

    if (formData.mode === 'single') {
      if (!formData.startDate || (!formData.allDay && !formData.startTime)) {
        showError('Completa fecha y hora del bloqueo único');
        return false;
      }

      return true;
    }

    if (!formData.recurrenceStartDate || (!formData.allDay && !formData.startTime)) {
      showError('Completa la fecha de inicio y hora de la regla recurrente');
      return false;
    }

    if (formData.recurrenceFrequency === 'WEEKLY' && formData.recurrenceDays.length === 0) {
      showError('Selecciona al menos un día de la semana');
      return false;
    }

    return true;
  };

  const buildPayload = () => {
    const payload = {
      doctorId: formData.doctorId,
      allDay: formData.allDay,
      durationMinutes: formData.allDay ? 1440 : Number(formData.durationMinutes),
      reason: formData.reason.trim()
    };

    if (formData.mode === 'single') {
      payload.startDateTime = buildSingleStartDateTime(formData.startDate, formData.allDay ? '00:00' : formData.startTime, timezone);
      return payload;
    }

    payload.startTime = formData.allDay ? '00:00' : formData.startTime;
    payload.rrule = buildRRule(formData, timezone);
    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const payload = buildPayload();

      if (isEdit) {
        await CalendarBlockService.updateCalendarBlock(blockId, payload, auth.user.access_token);
      } else {
        await CalendarBlockService.createCalendarBlock(payload, auth.user.access_token);
      }

      navigate(`/appointments/blocks?doctorId=${formData.doctorId}`, {
        state: {
          message: isEdit ? 'Bloqueo actualizado correctamente' : 'Bloqueo creado correctamente'
        }
      });
    } catch (error) {
      showError(error.message || (isEdit ? 'No se pudo actualizar el bloqueo' : 'No se pudo crear el bloqueo'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingBlock) return;

    setIsSaving(true);
    try {
      await CalendarBlockService.deleteCalendarBlock(existingBlock.id, {
        doctorId: existingBlock.doctorId
      }, auth.user.access_token);

      navigate(`/appointments/blocks?doctorId=${existingBlock.doctorId}`, {
        state: {
          message: existingBlock.type === 'BLOCK_RECURRING'
            ? 'Regla recurrente eliminada correctamente'
            : 'Bloqueo eliminado correctamente'
        }
      });
    } catch (error) {
      showError(error.message || 'No se pudo eliminar el bloqueo');
      setIsSaving(false);
    }
  };

  if (isLoadingDoctors || timezoneLoading || isLoadingBlock) {
    return (
      <div className="calendar-blocks-container">
        <div className="calendar-blocks-panel calendar-blocks-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-blocks-container">
      <div className="calendar-blocks-header">
        <div className="header-content">
          <h1>{isEdit ? 'Editar bloqueo' : 'Nuevo bloqueo'}</h1>
          <p>
            {isEdit
              ? 'Ajusta horarios, duración y recurrencia del bloqueo.'
              : 'Marca tiempos no disponibles para evitar citas manualmente.'}
          </p>
        </div>
        <div className="calendar-blocks-header-actions">
          <Button variant="secondary" onClick={() => navigate(`/appointments/blocks?doctorId=${formData.doctorId || doctorId || ''}`)}>
            Volver a bloqueos
          </Button>
        </div>
      </div>

      <div className="calendar-blocks-panel calendar-block-form-panel">
        <form className="calendar-blocks-form" onSubmit={handleSubmit}>
          {!isEdit && (
            <section className="calendar-blocks-section">
              <div className="calendar-blocks-section-title">Tipo de bloqueo</div>
              <div className="calendar-blocks-type-toggle">
                <button
                  type="button"
                  className={`calendar-blocks-toggle-option ${formData.mode === 'single' ? 'active' : ''}`}
                  onClick={() => handleToggleMode('single')}
                >
                  <div className="calendar-blocks-toggle-title">Bloqueo único</div>
                  <div className="calendar-blocks-toggle-copy">Bloquea un solo horario en una fecha específica.</div>
                </button>
                <button
                  type="button"
                  className={`calendar-blocks-toggle-option ${formData.mode === 'recurring' ? 'active' : ''}`}
                  onClick={() => handleToggleMode('recurring')}
                >
                  <div className="calendar-blocks-toggle-title">Bloqueo recurrente</div>
                  <div className="calendar-blocks-toggle-copy">Bloquea horarios que se repiten por día, semana o mes.</div>
                </button>
              </div>
            </section>
          )}

          <section className="calendar-blocks-section">
            <div className="calendar-blocks-section-title">Datos generales</div>
            <div className="calendar-blocks-grid full">
              <div className="calendar-blocks-form-group">
                <label htmlFor="doctorId">Doctor</label>
                <select
                  id="doctorId"
                  name="doctorId"
                  className="calendar-blocks-select"
                  value={formData.doctorId}
                  onChange={handleInputChange}
                  disabled={isEdit || !canViewAll}
                >
                  {doctors.map((doctor) => {
                    const resolvedDoctorId = doctor.id || doctor.userId;
                    const resolvedDoctorName = doctor.fullName || doctor.name || doctor.username || doctor.email;
                    return (
                      <option key={resolvedDoctorId} value={resolvedDoctorId}>
                        {resolvedDoctorName}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div className="calendar-blocks-grid full">
              <div className="calendar-blocks-form-group">
                <label htmlFor="reason">Motivo</label>
                <textarea
                  id="reason"
                  name="reason"
                  className="calendar-blocks-textarea"
                  placeholder="Ejemplo: junta de equipo, capacitación, comida, vacaciones"
                  value={formData.reason}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </section>

          {formData.mode === 'single' ? (
            <section className="calendar-blocks-section">
              <div className="calendar-blocks-section-title">Fecha del bloqueo único</div>
              <div className="calendar-blocks-grid full">
                <div className="calendar-blocks-form-group">
                  <label htmlFor="startDate">Fecha</label>
                  <input
                    id="startDate"
                    name="startDate"
                    type="date"
                    className="calendar-blocks-input"
                    value={formData.startDate}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </section>
          ) : (
            <section className="calendar-blocks-section">
              <div className="calendar-blocks-section-title">Regla recurrente</div>
              <div className="calendar-blocks-grid">
                <div className="calendar-blocks-form-group">
                  <label htmlFor="recurrenceStartDate">Empieza el</label>
                  <input
                    id="recurrenceStartDate"
                    name="recurrenceStartDate"
                    type="date"
                    className="calendar-blocks-input"
                    value={formData.recurrenceStartDate}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="calendar-blocks-form-group">
                  <label htmlFor="recurrenceUntil">Finaliza el</label>
                  <input
                    id="recurrenceUntil"
                    name="recurrenceUntil"
                    type="date"
                    className="calendar-blocks-input"
                    value={formData.recurrenceUntil}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="calendar-blocks-grid">
                <div className="calendar-blocks-form-group">
                  <label htmlFor="recurrenceFrequency">Frecuencia</label>
                  <select
                    id="recurrenceFrequency"
                    name="recurrenceFrequency"
                    className="calendar-blocks-select"
                    value={formData.recurrenceFrequency}
                    onChange={handleInputChange}
                  >
                    <option value="DAILY">Diaria</option>
                    <option value="WEEKLY">Semanal</option>
                    <option value="MONTHLY">Mensual</option>
                  </select>
                </div>
                <div className="calendar-blocks-form-group">
                  <label htmlFor="recurrenceInterval">Intervalo</label>
                  <input
                    id="recurrenceInterval"
                    name="recurrenceInterval"
                    type="number"
                    min="1"
                    className="calendar-blocks-input"
                    value={formData.recurrenceInterval}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {formData.recurrenceFrequency === 'WEEKLY' && (
                <div className="calendar-blocks-form-group calendar-blocks-weekday-section">
                  <label>Días de la semana</label>
                  <div className="calendar-blocks-weekday-group">
                    {WEEKDAY_OPTIONS.map((day) => (
                      <button
                        key={day.value}
                        type="button"
                        className={`calendar-blocks-weekday ${formData.recurrenceDays.includes(day.value) ? 'active' : ''}`}
                        onClick={() => handleWeekdayToggle(day.value)}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          <section className="calendar-blocks-section">
            <div className="calendar-blocks-section-title">Duración y horario</div>
            <div className="calendar-blocks-grid full">
              <div className="calendar-blocks-checkbox-card">
                <label htmlFor="allDay" className="calendar-blocks-checkbox-label">
                  <input
                    id="allDay"
                    name="allDay"
                    type="checkbox"
                    checked={formData.allDay}
                    onChange={handleAllDayChange}
                  />
                  <span>
                    <strong>Todo el día</strong>
                    <small>El bloqueo ocupará la jornada completa del doctor en la fecha o recurrencia indicada.</small>
                  </span>
                </label>
              </div>
            </div>

            {isAllDay ? (
              <p className="calendar-blocks-note">Se guardará con duración automática de 24 horas y hora de inicio 00:00.</p>
            ) : (
              <div className="calendar-blocks-grid three">
                <div className="calendar-blocks-form-group">
                  <label htmlFor="startTime">Hora de inicio</label>
                  <input
                    id="startTime"
                    name="startTime"
                    type="time"
                    className="calendar-blocks-input"
                    value={formData.startTime}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="calendar-blocks-form-group">
                  <label htmlFor="durationMinutes">Duración en minutos</label>
                  <input
                    id="durationMinutes"
                    name="durationMinutes"
                    type="number"
                    min="5"
                    step="5"
                    className="calendar-blocks-input"
                    value={formData.durationMinutes}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            )}
          </section>

          {isEdit ? (
            <div className="calendar-blocks-form-actions">
              <Button type="button" variant="secondary" onClick={() => navigate(`/appointments/blocks?doctorId=${formData.doctorId || doctorId || ''}`)}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" loading={isSaving}>
                Guardar cambios
              </Button>
            </div>
          ) : (
            <section className="calendar-blocks-section">
              <div className="calendar-blocks-section-title">Resumen</div>
              <p className="calendar-blocks-list-copy">
                Este bloqueo quedará asignado a <strong>{selectedDoctorName}</strong>.
              </p>
              <p className="calendar-blocks-note">
                Se usará automáticamente la zona horaria configurada para el doctor.
              </p>
              <p className="calendar-blocks-note">
                {formData.allDay
                  ? 'Se marcará como un evento de todo el día.'
                  : `Se bloquearán ${formData.durationMinutes} minutos a partir de las ${formData.startTime}.`}
              </p>
              <div className="calendar-blocks-form-actions">
                <Button type="button" variant="secondary" onClick={() => navigate(`/appointments/blocks?doctorId=${formData.doctorId || doctorId || ''}`)}>
                  Cancelar
                </Button>
                <Button type="submit" variant="primary" loading={isSaving}>
                  Crear bloqueo
                </Button>
              </div>
            </section>
          )}

          {isEdit && existingBlock && (
            <section className="calendar-blocks-section">
              <div className="calendar-blocks-section-title">Eliminar bloqueo</div>
              <p className="calendar-blocks-danger-copy">
                {existingBlock.type === 'BLOCK_RECURRING'
                  ? 'Esta acción elimina la regla completa y todas sus futuras ocurrencias.'
                  : 'Esta acción elimina el bloqueo único seleccionado.'}
              </p>
              <div className="calendar-blocks-danger-actions">
                <Button type="button" variant="danger" onClick={handleDelete} loading={isSaving}>
                  {existingBlock.type === 'BLOCK_RECURRING' ? 'Eliminar regla recurrente' : 'Eliminar bloqueo'}
                </Button>
              </div>
            </section>
          )}
        </form>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default CalendarBlockForm;